import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { convertDecimalToPrice } from 'services/utils/convertDecimalToPrice';
import { formatPercentage } from 'services/utils/amountFormatting';
import '../../style/scss/modals.scss';

// Colors of the work and travel hour segments matching the running timer colors
const WORK_HOURS_COLOR = 'var(--running_work_timer)';
const TRAVEL_HOURS_COLOR = 'var(--running_travel_timer)';

const JobHighlightsCard: React.FC<{
    summaryData: any;
}> = ({
    summaryData,
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Get the numeric amounts of the summary data
    const revenueValue = Number(summaryData.revenue ?? 0);
    const purchaseValue = Number(summaryData.purchase ?? 0);
    const marginValue = Number(summaryData.margin ?? 0);

    // Format the amounts of the summary data
    const revenue = convertDecimalToPrice(revenueValue.toString(), summaryData.currency, userLocale);
    const purchase = convertDecimalToPrice(purchaseValue.toString(), summaryData.currency, userLocale);
    const margin = convertDecimalToPrice(marginValue.toString(), summaryData.currency, userLocale);

    // Format the margin percentage
    const marginPercentage = formatPercentage(summaryData.margin_percentage, userLocale);

    // Determine whether the margin is negative
    const hasNegativeMargin = marginValue < 0;

    // Size the margin bar on the revenue, or on the costs when they exceed the revenue
    const marginBarTotal = hasNegativeMargin ? purchaseValue : revenueValue;

    // Build the margin bar segments showing how the costs and margin make up the revenue
    const marginBarSegments = hasNegativeMargin
        ? [
            { key: 'revenue', value: revenueValue, className: 'neutral', tooltip: `${t('job.summary.revenue_label')}: ${revenue}` },
            { key: 'margin', value: purchaseValue - revenueValue, className: 'negative', tooltip: `${t('job.summary.purchase_label')}: ${purchase}\n${t('job.summary.margin_label')}: ${margin}` },
        ]
        : [
            { key: 'purchase', value: purchaseValue, className: 'neutral', tooltip: `${t('job.summary.purchase_label')}: ${purchase}` },
            { key: 'margin', value: marginValue, className: 'positive', tooltip: `${t('job.summary.margin_label')}: ${margin}` },
        ];

    // Get the numeric hour values of the summary data
    const workHours = Number(summaryData.registered_work_hours ?? 0);
    const travelHours = Number(summaryData.registered_travel_hours ?? 0);
    const totalHours = Number(summaryData.registered_total_hours ?? 0);
    const estimatedHours = summaryData.estimated_hours !== null && summaryData.estimated_hours !== undefined
        ? Number(summaryData.estimated_hours)
        : null;

    // Format an hour value rounded to two decimals with the user locale
    const formatHours = (hours: number) => hours.toLocaleString(userLocale, { maximumFractionDigits: 2 });

    // Size the hours bar on the estimated hours, or on the registered total when it is higher
    const hoursBarTotal = Math.max(estimatedHours ?? 0, totalHours);

    // Calculate the registered percentage of the estimated hours
    const estimatedPercentage = estimatedHours && estimatedHours > 0
        ? Math.round((totalHours / estimatedHours) * 100)
        : null;

    // Build the hours bar segments for the registered work and travel hours
    const hoursBarSegments = [
        { key: 'work', value: workHours, color: WORK_HOURS_COLOR, tooltip: `${t('job.summary.work_hours_label')}: ${formatHours(workHours)}` },
        { key: 'travel', value: travelHours, color: TRAVEL_HOURS_COLOR, tooltip: `${t('job.summary.travel_hours_label')}: ${formatHours(travelHours)}` },
    ];

    return (
        <div className='summary-object-card'>
            <h6>{t('job.summary.highlights_label')}</h6>
            <div className='summary-card-section'>
                <div className='summary-card-section-header'>
                    <span className='section-label'>{t('job.summary.margin_label')}</span>
                    <span className={`section-value ${hasNegativeMargin ? 'negative' : ''}`}>
                        {margin}
                        {marginPercentage !== null && ` (${marginPercentage}%)`}
                    </span>
                </div>
                {marginBarTotal > 0 && (
                    <>
                        <div className='summary-card-bar'>
                            {marginBarSegments.filter(segment => segment.value > 0).map(segment => (
                                <div key={segment.key}
                                    className={`summary-card-bar-segment tooltip-icon ${segment.className}`}
                                    style={{ width: `${(segment.value / marginBarTotal) * 100}%` }}>
                                    <span className='tooltip'>{segment.tooltip}</span>
                                </div>
                            ))}
                        </div>
                        <div className='summary-card-note split'>
                            <span>{t('job.summary.purchase_label')} {purchase}</span>
                            <span>{t('job.summary.revenue_label')} {revenue}</span>
                        </div>
                    </>
                )}
            </div>
            <div className='summary-card-section'>
                <div className='summary-card-section-header'>
                    <span className='section-label'>{t('job.summary.registered_total_hours_label')}</span>
                    <span className='section-value'>{formatHours(totalHours)}</span>
                </div>
                {hoursBarTotal > 0 && (
                    <div className='summary-card-bar'>
                        {hoursBarSegments.filter(segment => segment.value > 0).map(segment => (
                            <div key={segment.key}
                                className='summary-card-bar-segment tooltip-icon'
                                style={{ width: `${(segment.value / hoursBarTotal) * 100}%`, backgroundColor: segment.color }}>
                                <span className='tooltip'>{segment.tooltip}</span>
                            </div>
                        ))}
                    </div>
                )}
                {estimatedHours !== null && estimatedHours > 0 && (
                    <div className='summary-card-note'>
                        {t('job.summary.estimated_hours_label')}: {formatHours(estimatedHours)}
                        {estimatedPercentage !== null && ` (${estimatedPercentage}%)`}
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobHighlightsCard;
